import { z } from 'zod'

import type { Profession } from 'src/shared/features/users/schemas/profession'
import { professionSchema } from 'src/shared/features/users/schemas/profession'

export const referralShareStatusMap = {
  pending: { label: 'Pending' },
  receiver_declined: { label: 'Declined By Receiver' },
  interested: { label: 'Interested' },
  approved: { label: 'Approved' },
  sender_denied: { label: 'Denied By Sender' },
  expired: { label: 'Expired' },
}

export const referralShareStatusSchema = z.enum(
  Object.keys(referralShareStatusMap) as [ReferralShareStatus, ...ReferralShareStatus[]],
)

export type ReferralShareStatus =
  | 'pending'
  | 'receiver_declined'
  | 'interested'
  | 'approved'
  | 'sender_denied'
  | 'expired'

interface BonusPointsDetail {
  points: number
  reason: string
}

interface BonusPoints {
  referral: BonusPointsDetail
  sender: BonusPointsDetail
  receiver: BonusPointsDetail
}

// Bonus Points schemas
const bonusPointsDetailSchema = z.object({
  points: z.number(),
  reason: z.string(),
})

const bonusPointsSchema = z.object({
  referral: bonusPointsDetailSchema,
  sender: bonusPointsDetailSchema,
  receiver: bonusPointsDetailSchema,
})

// ReferralShareData schema
export const referralShareSchema = z.object({
  id: z.number(),
  referralId: z.number(),
  sentAt: z.date(),
  interestedAt: z.date().nullable(),
  updatedAt: z.date(),
  status: referralShareStatusSchema,
  receiverTimeLeft: z.number(),
  senderTimeLeft: z.number().nullable(),
  receiverProfileUrl: z.string().url().nullable(),
  senderProfileUrl: z.string().url().nullable(),
  isClosed: z.boolean(),
  competitionCount: z.number(),
  clientFirstName: z.string(),
  clientLastName: z.string(),
  propertyStreetAddress1: z.string().nullable(),
  propertyStreetAddress2: z.string().nullable(),
  propertyCity: z.string().nullable(),
  propertyState: z.string().nullable(),
  propertyZip: z.string().nullable(),
  desiredLocations: z.array(z.string()).nullable(),
  senderId: z.number(),
  senderFirstName: z.string(),
  senderLastName: z.string(),
  senderLocation: z.string(),
  senderProfession: professionSchema,
  receiverId: z.number(),
  receiverFirstName: z.string(),
  receiverLastName: z.string(),
  receiverLocation: z.string(),
  receiverProfession: professionSchema,
  bonusPoints: bonusPointsSchema,
  receiverTotalPoints: z.number(),
  senderTotalPoints: z.number(),
})

export interface ReferralShareData {
  clientFirstName: string
  clientLastName: string
  propertyStreetAddress1: string | null
  propertyStreetAddress2: string | null
  propertyCity: string | null
  propertyState: string | null
  propertyZip: string | null
  desiredLocations: string[] | null
  senderId: number
  senderFirstName: string
  senderLastName: string
  senderLocation: string
  senderProfession: Profession
  receiverId: number
  receiverFirstName: string
  receiverLastName: string
  receiverLocation: string
  receiverProfession: Profession
  bonusPoints: BonusPoints
  receiverTotalPoints: number
  senderTotalPoints: number
}

export interface ReferralShare extends ReferralShareData {
  id: number
  referralId: number
  sentAt: Date
  interestedAt: Date | null
  updatedAt: Date
  status: ReferralShareStatus
  receiverTimeLeft: number
  senderTimeLeft: number | null
  receiverProfileUrl: string | null
  senderProfileUrl: string | null
  isClosed: boolean
  competitionCount: number
}
